import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtTime, apiError } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { CalendarClock, Building2, User, MapPin, ChevronRight, CalendarDays, Check } from "lucide-react";
import { EsitoModal, apptNeedsEsito } from "@/components/EsitoModal";

const TIPO_LABEL = {
  appuntamento_acquisizione: "Acquisizione", valutazione: "Valutazione", visita: "Visita",
  appuntamento_cliente: "Cliente", proposta: "Proposta", preliminare: "Preliminare",
  rogito: "Rogito", follow_up: "Follow-up", riunione: "Riunione", chiamata: "Chiamata",
};
const tipoLabel = (t) => TIPO_LABEL[t] || (t || "").replace(/_/g, " ") || "Altro";

const STATO_CLS = {
  programmato: "text-sky-600 bg-sky-400/10 border-sky-400/30",
  annullato: "text-slate-400 bg-slate-100 border-slate-300 line-through",
};
const STATO_LABEL = { programmato: "Programmato", annullato: "Annullato" };
const statoLabel = (s) => STATO_LABEL[s] || (s || "").replace(/_/g, " ") || "—";

export default function AppuntamentiOggi() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [esitoAppt, setEsitoAppt] = useState(null);

  const load = () => api.get("/dashboard/appuntamenti-oggi").then((r) => setItems(r.data)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  if (!items) return <div className="skeleton h-96 rounded-2xl" />;

  const perTipo = items.reduce((acc, x) => { const t = x.tipo || "altro"; acc[t] = (acc[t] || 0) + 1; return acc; }, {});
  const tipiOrdinati = Object.entries(perTipo).sort((a, b) => b[1] - a[1]);

  return (
    <div className="fade-up" data-testid="appuntamenti-oggi-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle sub="Tutti gli appuntamenti in programma per la giornata di oggi, con orario, immobile, agente e stato.">Appuntamenti di oggi</SectionTitle>
        <button data-testid="apt-oggi-agenda-btn" onClick={() => nav("/agenda")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[var(--border)] text-sm font-semibold hover:border-[#FC82ED]/40 shrink-0">
          <CalendarDays size={16} className="text-[#FC82ED]" /> Vai all'agenda
        </button>
      </div>

      {items.length > 0 && (
        <div data-testid="apt-oggi-summary" className="flex flex-wrap items-center gap-2 mt-5">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-bold">
            <CalendarClock size={14} /> {items.length} appuntament{items.length === 1 ? "o" : "i"} oggi
          </span>
          {tipiOrdinati.map(([t, n]) => (
            <span key={t} data-testid={`apt-summary-${t}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold text-slate-600">
              {tipoLabel(t)} <b className="text-[#111111]">{n}</b>
            </span>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <div className="card-immo p-12 text-center mt-5" data-testid="apt-oggi-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#FC82ED]/12 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] mx-auto mb-4"><CalendarClock size={26} /></div>
          <p className="text-[var(--muted)] text-sm">Nessun appuntamento in programma oggi.</p>
        </div>
      ) : (
        <div className="space-y-3 mt-4" data-testid="apt-oggi-list">
          {items.map((a) => (
            <div key={a.id} data-testid={`apt-oggi-${a.id}`}
              onClick={() => a.immobile_id && nav(`/immobili/${a.immobile_id}`)}
              className={`card-immo p-4 flex items-center gap-4 flex-wrap ${a.immobile_id ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : ""}`}>
              <div className="mono text-lg font-bold text-[#FC82ED] w-14 shrink-0 text-center">{fmtTime(a.data)}</div>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold text-sm truncate flex items-center gap-2">
                  <Building2 size={14} className="text-[#FC82ED] shrink-0" />
                  {a.titolo || a.immobile_titolo || tipoLabel(a.tipo)}
                </div>
                <div className="text-[11px] text-[var(--muted)] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                  <span className="uppercase tracking-wide">{tipoLabel(a.tipo)}</span>
                  {a.cliente_nome && <span className="flex items-center gap-1"><User size={11} /> {a.cliente_nome}</span>}
                  <span className="flex items-center gap-1"><User size={11} /> Agente: <b className="text-[#111111]">{a.agente || "—"}</b></span>
                  {(a.indirizzo || a.immobile_titolo) && <span className="flex items-center gap-1 min-w-0"><MapPin size={11} className="shrink-0" /> <span className="truncate">{a.indirizzo || a.immobile_titolo}</span></span>}
                </div>
              </div>
              <span data-testid={`apt-stato-${a.id}`} className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase border shrink-0 ${STATO_CLS[a.stato] || "text-slate-500 bg-slate-100 border-slate-300"}`}>{statoLabel(a.stato)}</span>
              <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                {apptNeedsEsito(a) ? (
                  <button data-testid={`apt-esito-btn-${a.id}`} onClick={() => setEsitoAppt(a)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold bg-[#FC82ED]/15 text-[#111111] border-[#FC82ED]/40 hover:bg-[#FC82ED]/25 transition-colors">
                    <Check size={13} /> Com'è andata?
                  </button>
                ) : a.esito_registrato ? (
                  <span data-testid={`apt-esito-done-${a.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200">
                    <Check size={13} /> Esito ✓
                  </span>
                ) : null}
              </div>
              {a.immobile_id && <ChevronRight size={16} className="text-[var(--muted)] shrink-0" />}
            </div>
          ))}
        </div>
      )}
      {esitoAppt && <EsitoModal appt={esitoAppt} onClose={() => setEsitoAppt(null)} onSaved={() => { setEsitoAppt(null); load(); }} />}
    </div>
  );
}
